import { tuple } from "functions/FunnyTypes";
import { FitsSchema, SchemaToType } from "functions/networking/JsonSchema";


export const TYPE_PLA = "PLA33RXE" as const;
export const TYPE_GCR = "GCR12RXE" as const;
export const TYPE_GCRT = "GCR12TRXE" as const;
export const TYPE_ICR = "ICR12RXE" as const;

export type SupportedDevices = typeof TYPE_PLA | typeof TYPE_GCR | typeof TYPE_GCRT | typeof TYPE_ICR;

export type HarmSimple = `Harm_${"U_LL" | "U_LN" | "Current"}${"_Percent" | ""}`;
type HarmAll = `${HarmSimple}${"_Max" | "_Avg" | ""}`

type Online = `Online${"_Min" | "_Max" | "_Avg" | "_Short" | ""}`

export type Fetchable<D extends SupportedDevices>
= D extends typeof TYPE_PLA ? Online | HarmAll | "Energy_Per_Day_Folder_Info" | "Energy_Folder_Info" | `Energy${"_Per_Day" | ""}_File` | "IO_State"
: D extends typeof TYPE_GCR ? "Online" | "Online_Short" | `Harm_${"U_LL"|"Current"}${""|"_Percent"}${""|"_Max"}` | "Meas" | "Stages" | "Alarms"
: D extends typeof TYPE_ICR ? "Online" | "Online_Short" | "Meas" | "Stages" | "Alarms"
: never;

/*
export const GetRequests = {
	[TYPE_PLA]: [
		"Online",
		"Online_Min",
		"Online_Max",
		"Online_Avg",
		"Online_Short",

		"Harm_U_LL",         "Harm_U_LL_Max",         "Harm_U_LL_Avg",
		"Harm_U_LL_Percent", "Harm_U_LL_Percent_Max", "Harm_U_LL_Percent_Avg",

		"Harm_U_LN",         "Harm_U_LN_Max",         "Harm_U_LN_Avg",
		"Harm_U_LN_Percent", "Harm_U_LN_Percent_Max", "Harm_U_LN_Percent_Avg",

		"Harm_U_Current",         "Harm_U_Current_Max",         "Harm_U_Current_Avg",
		"Harm_U_Current_Percent", "Harm_U_Current_Percent_Max", "Harm_U_Current_Percent_Avg",

		// "Energy_Per_Day_Folder_Info", "Energy_Per_Day_File",
		// "Energy_Folder_Info",         "Energy_File",

		"IO_State",
	] as Fetchable<typeof TYPE_PLA>[],
	[TYPE_GCR]: [
		"Online",
		"Online_Short",

		"Harm_U_LL",         "Harm_U_LL_Max",
		"Harm_U_LL_Percent", "Harm_U_LL_Percent_Max",

		"Harm_U_Current",         "Harm_U_Current_Max",
		"Harm_U_Current_Percent", "Harm_U_Current_Percent_Max",

		"Meas",
		"Stages",
	] as Fetchable<typeof TYPE_GCR>[],
} as const;
*/

export type FetchOptions<D extends SupportedDevices, T extends Fetchable<D>>
= T extends `Energy${"_Per_Day" | ""}_File` ? { File_ID : number, File_Count : number, Tariff_Count : number }
: null;

export type SetOptions = {
  IO_State: { [k in `${keyof IO_State}_On`] : boolean }
}

const string = "string" as const;
const number = "number" as const;
const bool = "boolean" as const;
const gcrtime = string;

export const DeviceInfoPLASchema = {
  Name: `lit(${TYPE_PLA})`,
	Description: string,
	Firmware: string,
	Version: string,
	Mqtt_uid: string,
	Equipment: {
		RS485: bool,
		Profibus: bool,
		Data_flash: bool,
		Virtual_flash: bool,
		FRam: bool,
		RJ45: bool,
		Analog_output: bool,
		Relay_outputs: number,
		Analog_inputs_outputs: number,
		Analog_outputs: number
	},
	_type: `lit(${TYPE_PLA})`,
} as const;

export type DeviceInfoPLA = SchemaToType<typeof DeviceInfoPLASchema>;

export const DeviceInfoGCRSchema = {
	Name: string,
	Description: string,
	Firmware: string,
	Mqtt_uid: string,
	Equipment: {},
	_type: `lit(${TYPE_GCR})`,
} as const;

export const DeviceInfoGCRTSchema = {
	Name: string,
	Description: string,
	Firmware: string,
	Mqtt_uid: string,
	Equipment: {},
	_type: `lit(${TYPE_GCRT})`,
} as const;

export const DeviceInfoICRSchema = {
	Name: string,
	Description: string,
	Firmware: string,
	Mqtt_uid: string,
	Equipment: {},
	_type: `lit(${TYPE_ICR})`,
} as const;

type DeviceInfoGCR = SchemaToType<typeof DeviceInfoGCRSchema>;
type DeviceInfoGCRT = SchemaToType<typeof DeviceInfoGCRTSchema>;
type DeviceInfoICR = SchemaToType<typeof DeviceInfoICRSchema>;

export type DeviceInfo<D extends SupportedDevices>
= D extends typeof TYPE_PLA ? DeviceInfoPLA
: D extends typeof TYPE_GCR ? DeviceInfoGCR
: D extends typeof TYPE_GCRT ? DeviceInfoGCRT
: D extends typeof TYPE_ICR ? DeviceInfoICR
: never;

export function IsDeviceInfo(o: any) : o is DeviceInfo<SupportedDevices> {
	if (typeof o !== "object") return false;
	if (typeof o.Name !== "string") return false;
	o._type = ParseDeviceName(o.Name);
	return FitsSchema(o, DeviceInfoPLASchema)
    || FitsSchema(o, DeviceInfoGCRSchema)
    || FitsSchema(o, DeviceInfoGCRTSchema)
    || FitsSchema(o, DeviceInfoICRSchema);
}


export type ResponseHead<D extends SupportedDevices> = {
  Connection: string | "3UN_3I",
  Date_time: D extends typeof TYPE_PLA ? number : undefined,
  File_state: string | "Actual",
  File_type: Fetchable<D>,
  Device: {
    Description: string,
    Firmware: string,
    Name: string,
    Version?: string,
  },
  _type: D,
}

export function IsResponseHead(o: any) : o is ResponseHead<SupportedDevices> {
	if (typeof o !== "object") return false;
	if (typeof o.Device !== "object") return false;
	if (typeof o.Device.Name !== "string") return false;

  o._type = ParseDeviceName(o.Device.Name);
  return typeof o.File_state === "string"
      && typeof o.File_type === "string"
      && typeof o.Device.Description === "string"
      && typeof o.Device.Firmware === "string"
      && o._type;
}

export function ParseDeviceName(name: string) : SupportedDevices | null {
  if (name === "PLA33RXE") return TYPE_PLA;

  if (name.startsWith("ICR")) return TYPE_ICR;

  if (!name.startsWith("GCR")) return null;
  if (name.includes("T")) return TYPE_GCRT;
  return TYPE_GCR;
}

export type HarmResponse = ReturnType<typeof ParseHarm>;

function IsN3(a: any) : a is [number, number, number] {
  return a instanceof Array && a.length === 3 && a.every(v => typeof v === "number");
}


export type energy = {Consumption: {Active_kWh: number, Inductive_kvarh: number, Capacity_kvarh: number}, Distribution: {Active_kWh: number, Inductive_kvarh: number, Capacity_kvarh: number}};

function IsEnergy(o: any) : o is energy {
  return typeof o === "object"
      && typeof o.Consumption === "object"
      && typeof o.Consumption.Active_kWh === "number"
      && typeof o.Consumption.Inductive_kvarh === "number"
      && typeof o.Consumption.Capacity_kvarh === "number"
      && typeof o.Distribution === "object"
      && typeof o.Distribution.Active_kWh === "number"
      && typeof o.Distribution.Inductive_kvarh === "number"
      && typeof o.Distribution.Capacity_kvarh === "number";
}

const shortKeys = [
  "CosFi", "CosFi_3F",
  "CosFi_Mod", "CosFi_3F_Mod",
  "Current", "Current_Zero",
  "Frequency",
  "P", "P_3F",
  "Q", "Q_3F",
  "S", "S_3F",
  "Temperature",
  "Thd_Current", "Thd_U_LL", "Thd_U_LN",
  "U_LL",
  "U_LN"
] as const;

const onlineMAMkeys = [
  "Angle_I", "Angle_U",
  "CosFi", "CosFi_3F",
  "CosFi_Mod", "CosFi_3F_Mod",
  "Pf", "Pf_3F",
  "Pf_Mod", "Pf_3F_Mod",
  "Current", "Current_Zero", "Distortion_Power",
  "Frequency", "KFactor",
  "P", "P_3F",
  "Q", "Q_3F", "S", "S_3F", "Tdd",
  "Temperature", "Thd_Current", "Thd_U_LL",
  "Thd_U_LN", "U_LL", "U_LN",
  "Unbalance_I_I0", "Unbalance_I_I2",
  "Unbalance_LN_U0", "Unbalance_LN_U2",

  "Rotation",
  "Over_Deviation_LL",
  "Over_Deviation_LN",
  "Under_Deviation_LL",
  "Under_Deviation_LN",
] as const;

export const energyKeys = [
  "Energy_T1", "Energy_T2", "Energy_T3", "Energy_T4",
  "Phase_energy_L1", "Phase_energy_L2", "Phase_energy_L3"
] as const;

export type OnlineDataPLA = { [_ in typeof onlineMAMkeys[number]] : [number, number, number] } & {
  Rotation : [number, number, number]
} & {[_ in typeof energyKeys[number]] : energy}

export type ShortOnlineData = { [_ in typeof shortKeys[number]]: tuple<number, 3> }

export const OnlineDataSchemaGCRShort = {
  CosFi:       [number],
  CosFi_Mod:   [number],  // pri dodavce je abs hodnota uciniku zvetesna o 1
  S:           [number],
  P:           [number],
  Q:           [number],

  U_LL:        [number],
  Thd_U_LL:    [number],
  Current:     [number],
  Thd_Current: [number],
  Frequency:   [number],
  Temperature: [number],
} as const;

export const OnlineDataSchemaGCR = {
  ...OnlineDataSchemaGCRShort,

  CosFi_Avg:   [number],
  Allowed_Q:   [number],
  Reqd_Q:      [number],
  Compd_Q:     [number],
  Total_Q:     [number],

  SHTD_p:      [number],
  // SHTD_m:      [number],
} as const;

export const OnlineDataSchemaICRShort = {
  CosFi:       [number],
  CosFi_Mod:   [number],  // pri dodavce je abs hodnota uciniku zvetesna o 1
  S:           [number],
  P:           [number],
  Q:           [number],

  Temperature: [number],
} as const;

export const OnlineDataSchemaICR = {
  ...OnlineDataSchemaICRShort,

  Allowed_Q:   [number],
  Reqd_Q:      [number],
  Compd_Q:     [number],
  Total_Q:     [number],

  SHTD_p:      [number],
  // SHTD_m:      [number],
} as const;

export type OnlineDataGCR = SchemaToType<typeof OnlineDataSchemaGCR>;
export type OnlineDataGCRShort = SchemaToType<typeof OnlineDataSchemaGCRShort>;
export type OnlineDataICR = SchemaToType<typeof OnlineDataSchemaICR>;
export type OnlineDataICRShort = SchemaToType<typeof OnlineDataSchemaICRShort>;
export type OnlineData = OnlineDataPLA | OnlineDataGCR | OnlineDataICR;

export function IsOnlineData(o: any) : o is OnlineData {
  return (typeof o === "object"
      && onlineMAMkeys.every(k => IsN3(o[k]))
      && energyKeys.every(k => IsEnergy(o[k])))
			|| (FitsSchema(o, OnlineDataSchemaGCR))
			|| (FitsSchema(o, OnlineDataSchemaICR));
}

export function IsShortOnlineData<D extends SupportedDevices>(o: any, dvc : D)
: o is D extends typeof TYPE_PLA ? ShortOnlineData : D extends typeof TYPE_ICR ? OnlineDataICRShort : OnlineDataGCRShort {
	switch (dvc) {
	case TYPE_PLA:
		return typeof o === "object" && shortKeys.every(k => IsN3(o[k]));
	case TYPE_GCR:
	case TYPE_GCRT:
		return FitsSchema(o, OnlineDataSchemaGCRShort);
  case TYPE_ICR:
		return FitsSchema(o, OnlineDataSchemaICRShort);
	}

	return false;
}

export type DayFile = {
  Status : "Success" | "Error",
  Firmware : string,
  ID_detail? : number,
  Date_time : Date,
  Cleared : boolean,
  T1 : tuple<bigint, 6>,
  T2? : tuple<bigint, 6>,
  T3? : tuple<bigint, 6>,
  T4? : tuple<bigint, 6>,
}

type Option<T> = { success: true, value: T } | { success: false };
const FAIL = { success: false } as const;

const O = BigInt(0);
export const zero_6tupple = () => [O, O, O, O, O, O] as tuple<bigint, 6>;

function ParseCompactEnergy(o : any) : Option<tuple<bigint, 6>> {
  if (typeof o !== "object") return FAIL;
  let res = zero_6tupple();
  for (let i = 1; i <= 6; i++) {
    try {
      res[i - 1] = BigInt.asUintN(64, BigInt(`0x${o[i]}`));
    } catch {
      return FAIL;
    }
  }
  return { success: true, value: res };
}

const epochStart = new Date("Jan 01 2000 01:00:00 GMT+0100").valueOf();
function ToDate(v : number) {
  return new Date(epochStart + v);
}

function ParseDayFile(o : any) : Option<DayFile> {
  if (!(typeof o === "object"
    && (o.Status === "Success" || o.Status === "Error")
    && typeof o.Firmware === "string"
    && (typeof o.ID_detail === "number" || typeof o.ID_detail === "undefined")
    && typeof o.Date_time === "number"
    && typeof o.Cleared === "boolean"))
    return FAIL;

  o.Date_time = ToDate(o.Date_time);

  let res = {...o} as DayFile;
  for (let k of ["T1", "T2", "T3", "T4"] as const) {
    if (o[k] === undefined) {
      if (k === "T1") return FAIL;
      continue;
    }
    if (!o.Cleared) {
      let opt = ParseCompactEnergy(o[k]);
      if (!opt.success) return FAIL;
      res[k] = opt.value;
    } else {
      res[k] = zero_6tupple();
    }
  }

  return { success: true, value: res };
}

type DayFiles = { [ k : number ] : DayFile };

export function ParseDayFiles(o : any) : Option<DayFiles> {
  if (typeof o !== "object") return FAIL;
  let res = {} as DayFiles;
  for (let k in o) {
    let opt = ParseDayFile(o[k]);
    if (!opt.success) return FAIL;
    res[k as any] = opt.value;
  }

  return { success: true, value: res };
}


type RState = {
  Type: "Alarm_output" | "Digital_output",
  Output_state: "Off" | "On"
};
type KState = {
  Type : "Pulse_input",
  Units : string,
  Value : number,
  Weight : number
} | {
  Type : "Digital_input",
  Input_state : "Off" | "On",
} | {
  Type: "Digital_output",
  Output_state: "Off" | "On"
} | { Type : | "Pulse_output" | "Tariff_input" };
type IO_State = {
  [r in `R${1|2|3}`] : RState
} & {
  [k in `K${1|2|3|4}`] : KState
}
function IsRState(o : any) : o is KState {
  return typeof o === "object"
      && true;  // TODO:
}
function IsKState(o : any) : o is KState {
  return typeof o === "object"
      && true;  // TODO:
}
export function IsIOState(o : any) : o is IO_State {
  return typeof o === "object"
      && IsRState(o.R1)
      && IsRState(o.R2)
      && IsRState(o.R3)
      && IsKState(o.K1)
      && IsKState(o.K2)
      && IsKState(o.K3)
      && IsKState(o.K4);
}

export const gcr12_meas_schema = {
	cos: {
		avg:     [number],
		now_mod: [number],
		now:     [number],
		ind_con: [number],
		cap_con: [number],
		ind_del: [number],
		cap_del: [number],
	},

	// proud
	I: {
		now:      [number],
		max:      [number],
		maxt:     [gcrtime],
		thd:      [number],
		thd_max:  [number],
		thd_maxt: [gcrtime],
	},

	// napeti
	U_LL: {
		now:      [number],
		max:      [number],
		maxt:     [gcrtime],
		thd:      [number],
		thd_max:  [number],
		thd_maxt: [gcrtime],
	},

	// vykony
	power: {
		S:         [number],
		P:         [number],
		Q:         [number],
		allowed_Q: [number],
		S_max:     [number],
		P_max:     [number],
		Q_max:     [number],
		Q_min:     [number],
		S_maxt:    [gcrtime],
		P_maxt:    [gcrtime],
		Q_maxt:    [gcrtime],
		Q_mint:    [gcrtime],
	},

	// frekvence
	freq: {
		now:  [number],
		max:  [number],
		maxt: [gcrtime],
		min:  [number],
		mint: [gcrtime],
	},

	// teplota
	temp: {
		now:  [number],
		max:  [number],
		maxt: [gcrtime],
	}
} as const;
export type GCR12_Meas = SchemaToType<typeof gcr12_meas_schema>;

export const icr12_meas_schema = {
	cos: {
		avg:     [number],
		now_mod: [number],
		now:     [number],
		ind_con: [number],
		cap_con: [number],
		ind_del: [number],
		cap_del: [number],
	},

	// vykony
	power: {
		S:         [number],
		P:         [number],
		Q:         [number],
		allowed_Q: [number],
		S_max:     [number],
		P_max:     [number],
		Q_max:     [number],
		Q_min:     [number],
		S_maxt:    [gcrtime],
		P_maxt:    [gcrtime],
		Q_maxt:    [gcrtime],
		Q_mint:    [gcrtime],
	},

	// teplota
	temp: {
		now:  [number],
		max:  [number],
		maxt: [gcrtime],
	}
} as const;
export type ICR12_Meas = SchemaToType<typeof icr12_meas_schema>;

export const gcr12_stage_schema = [{
	time:      number,
	nr_op:     number,
	max_nr_op: number,
	pow:       number,
	state:     bool,
	fixed:     bool,
	fast:      bool,
	l:         number, // | undef
}] as const;
export type GCR12_Stages = SchemaToType<typeof gcr12_stage_schema>;

export const gcr12_alarms_schema = [{
	typ:  string,
	val:  number,
	time: gcrtime,
	l:    number,  // input phase (if applicable)
}] as const;
export type GCR12_Alarms = SchemaToType<typeof gcr12_alarms_schema>;

export function ParseHarm(raw: any, header: ResponseHead<SupportedDevices>) {
	let result = { nr_phases: 3, values: [] as (number|null)[][], times: [] as (string[] | undefined)[] };

	switch (header._type) {
	case TYPE_PLA: result.nr_phases = 3; break;
	case TYPE_GCR: result.nr_phases = 1; break;
	case TYPE_GCRT:
		if (header.File_type.includes("Current")) result.nr_phases = 3;
		else                                      result.nr_phases = 1;
		break;
	}

	for (let i = 1; i < 40; i++) {
		const id = i.toFixed(0).padStart(2, '0');

		const vals  = raw[`H_${id}`] as (number|null)[] | undefined;
		const times = raw[`T_${id}`] as (string)[] | undefined;
		if (vals === undefined) break;

		if (header._type === TYPE_GCRT) {
			if (result.nr_phases === 3) {
				vals[0] = null;
				result.values.push(vals);
				result.times.push(times);
			}
			else {
				result.values.push([vals[0]]);
				result.times.push([raw[`T_${id}`]?.[0]]);
			}
		}
		else {
			vals.unshift(null);
			result.values.push(vals);
			times?.unshift("");
			result.times.push(times);
		}
	}

	return result;
}


export type ParsableResponses<D extends SupportedDevices>
= D extends typeof TYPE_PLA ? `Online${any}` | `Harm${any}` | `Energy${"_Per_Day" | ""}_Folder_Info` | `Energy${"_Per_Day" | ""}_File` | "IO_State"
: D extends typeof TYPE_GCR ? `Online${any}` | `Harm${any}` | "Meas" | "Stages" | "Alarms"
: D extends typeof TYPE_ICR ? `Online${any}` | "Meas" | "Stages" | "Alarms"
: never;

export type DataType<D extends SupportedDevices, T extends ParsableResponses<SupportedDevices>>
= T extends "Online"
	? D extends typeof TYPE_PLA ? OnlineDataPLA : OnlineDataGCR
: T extends "Online_Short"
	? D extends typeof TYPE_PLA ? ShortOnlineData : OnlineDataGCRShort
: T extends `Online${any}` ? OnlineDataPLA
: T extends `Harm${any}` ? HarmResponse
: T extends `Energy${"_Per_Day" | ""}_Folder_Info` ? { Max_file_id : number, Min_file_id : number }
: T extends `Energy${"_Per_Day" | ""}_File` ? DayFiles
: T extends "IO_State" ? IO_State
: T extends "Meas" ? GCR12_Meas
: T extends "Stages" ? GCR12_Stages
: T extends "Alarms" ? GCR12_Alarms
: never;

export type RType<T extends Fetchable<SupportedDevices>, D extends SupportedDevices> = {
  Head : ResponseHead<D>,
  Data : DataType<D, T>
}

export function IsFromDevice<D extends SupportedDevices, T extends Fetchable<any>>(o: RType<T, SupportedDevices>, kind: D | D[]): o is RType<T, D> {
  if (Array.isArray(kind)) {
    return kind.some(d => o.Head._type === d);
  }
  else return o.Head._type === kind;
}

type succ<D extends SupportedDevices, T extends Fetchable<D>> = { err: false, val: RType<T, D> };

export type err_key = "send_fail" | "timeout" | "canceled" | "parse_fail" | "unknown"
type fail = { err: true, kind: err_key, msg?: string }

export type FetchResult<D extends SupportedDevices, T extends Fetchable<D>> = succ<D, T> | fail;
export type SetResult = { err: false } | fail;

export enum MsgModes {
  Get = 0,
  Set = 1,
}

export type FetchMessage<D extends SupportedDevices, T extends Fetchable<D>>
= {
  topic : string,
  request : T,
  res : (val : FetchResult<D, T>) => void,
  options : FetchOptions<D, T>,
  mode : MsgModes.Get
};

export type SetMessage<K extends keyof SetOptions> = {
  topic : string,
  request : K,
  res : (val : SetResult) => void,
  options : SetOptions[K],
  mode : MsgModes.Set
}

export type Message<D extends SupportedDevices> = FetchMessage<D, Fetchable<D>> | SetMessage<keyof SetOptions>;
